import { useEffect, useState } from 'react';
import { getProfileUrl, type ProfileLang } from '@/constants/companyProfile';

export type AvailabilityStatus = 'checking' | 'available' | 'missing';

export interface ProfileAvailability {
  fr: AvailabilityStatus;
  en: AvailabilityStatus;
}

/**
 * Probes the canonical Company Profile PDFs (FR + EN) with a HEAD request
 * to verify they are actually served before exposing the download UI.
 *
 * Result is cached in module scope so repeated mounts don't re-fetch.
 */
const cache: Partial<Record<ProfileLang, AvailabilityStatus>> = {};

async function probe(lang: ProfileLang): Promise<AvailabilityStatus> {
  if (cache[lang] && cache[lang] !== 'checking') return cache[lang]!;
  try {
    const res = await fetch(getProfileUrl(lang), {
      method: 'HEAD',
      cache: 'no-cache',
    });
    if (!res.ok) {
      cache[lang] = 'missing';
      return 'missing';
    }
    // Some servers don't return Content-Type for HEAD; accept any 2xx.
    const type = res.headers.get('content-type') || '';
    const length = Number(res.headers.get('content-length') || '0');
    if (type && !type.includes('pdf') && !type.includes('octet-stream')) {
      cache[lang] = 'missing';
      return 'missing';
    }
    if (length > 0 && length < 1024) {
      cache[lang] = 'missing';
      return 'missing';
    }
    cache[lang] = 'available';
    return 'available';
  } catch {
    cache[lang] = 'missing';
    return 'missing';
  }
}

export function useProfileAvailability(): ProfileAvailability {
  const [state, setState] = useState<ProfileAvailability>({
    fr: cache.fr ?? 'checking',
    en: cache.en ?? 'checking',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [fr, en] = await Promise.all([probe('fr'), probe('en')]);
      if (!cancelled) setState({ fr, en });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
